import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  StackDivider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import React, { useEffect } from 'react';
import logo from '../assets/image/logo.png';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { userLogin } from '../stores/slices/user/userActions';
import { useState } from 'react';

const schema = yup
  .object({
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required'),
    password: yup.string().required('Password is required'),
  })
  .required();

const Login = () => {
  const { error, userToken } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [errorList, setErrorList] = useState([]);
  // redirect authenticated user to dashboard
  useEffect(() => {
    if (userToken) {
      navigate('/dashboard');
    }
  }, [userToken, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async data => {
    await dispatch(userLogin(data));
  };

  useEffect(() => {
    if (searchParams.has('confirmation_token')) {
      if (searchParams.has('empty')) {
        toast({
          description:
            'Your confirmation token was not found, your account may have already been confirmed',
          status: 'info',
          duration: 8000,
          isClosable: true,
          position: 'bottom-right',
        });
      } else {
        toast({
          description: 'Your Account has been Confirmed!',
          colorScheme: 'primary',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'bottom-right',
          variant: 'primary',
        });
      }
    }
    if (searchParams.has('unsubscribe')) {
      toast({
        description: 'You have been unsubscribed',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  }, [searchParams]);
  useEffect(() => {
    if (error) {
      let list = [];
      Object.keys(error).forEach((key, index) => {
        list.push(
          `${key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')} ${
            error[key]
          }`
        );
      });
      setErrorList(list.join('<br />'));
    }
  }, [error]);
  return (
    <Stack spacing={8} mx={'auto'} maxW={'4xl'} w={'100%'} py={12} px={6}>
      <Box rounded={'lg'} bg="white" boxShadow={'lg'}>
        <Stack
          spacing={4}
          direction={{ base: 'column', md: 'row' }}
          justify={'space-between'}
          align={'center'}
          divider={<StackDivider borderColor="gray.200" />}
        >
          <Box p={8}>
            <Image
              src={logo}
              alt="4 boxes logo"
              boxSize="300px"
              objectFit="contain"
            />
          </Box>
          <Box p={8} w={'100%'} maxW={'sm'} mx="auto">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box>
                <Heading mb="10px">Sign In</Heading>
                <Text
                  fontSize={'md'}
                  mb="4"
                  ms="4px"
                  color="primary"
                  fontWeight="500"
                >
                  Enter your email and password to sign in
                </Text>
                {error && error.data ? (
                  <Box
                    bg={'red.200'}
                    p={4}
                    borderRadius="lg"
                    color={'white'}
                    mb={4}
                  >
                    {error.data.error}
                  </Box>
                ) : (
                  error && (
                    <Box
                      bg={'red.200'}
                      p={4}
                      borderRadius="lg"
                      color={'white'}
                      mb={4}
                    >
                      {errorList}
                    </Box>
                  )
                )}
              </Box>
              <Stack>
                <FormControl id="email" isInvalid={errors.email}>
                  <FormLabel htmlFor="email">Email address</FormLabel>
                  <Input id="email" {...register('email')} />
                  <FormErrorMessage>
                    {errors.email && errors.email.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl id="password" isInvalid={errors.password}>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <Input
                    type="password"
                    id="password"
                    {...register('password')}
                  />
                  <FormErrorMessage>
                    {errors.password && errors.password.message}
                  </FormErrorMessage>
                </FormControl>
                <Stack spacing={4}>
                  <Stack
                    direction={{ base: 'column', sm: 'row' }}
                    alignItems={'start'}
                    justifyContent={'start'}
                  >
                    <Link as={RouterLink} to="/forgot-password">
                      <Text color={'primary'} as="span" ms="5px">
                        Forgot Password?
                      </Text>
                    </Link>
                  </Stack>
                  <Button
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    type="submit"
                  >
                    Sign in
                  </Button>
                </Stack>
                <Flex
                  direction="column"
                  justifyContent="center"
                  alignItems="start"
                  maxW="100%"
                  mt="0px"
                >
                  <Text fontWeight="400" fontSize="14px">
                    Not registered yet?
                    <Link as={RouterLink} to="/signup">
                      <Text
                        color={'primary'}
                        as="span"
                        ms="5px"
                        fontWeight="500"
                      >
                        Create an Account
                      </Text>
                    </Link>
                  </Text>
                </Flex>
                <Text fontWeight="400" fontSize="14px">
                  <a
                    href="https://oasispc.com.au/privacy-policy"
                    target="_blank"
                  >
                    <Text color={'primary'} as="span">
                      Privacy Policy
                    </Text>
                  </a>
                </Text>
              </Stack>
            </form>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default Login;
